import { useRef } from 'react'
import { useFeedback } from '@/app/providers/FeedbackContext'
import { useEscapeKey } from '@/common/hooks/useEscapeKey'
import { useIsCompactLayout } from '@/common/hooks'
import type { FeedbackType } from '@/types/feedback'

const feedbackTypes: { value: FeedbackType; label: string; icon: string }[] = [
  { value: 'bug_report', label: 'Bug Report', icon: '🐛' },
  { value: 'feature_request', label: 'Feature Request', icon: '💡' },
  { value: 'chat_response', label: 'Chat Issue', icon: '💬' },
  { value: 'error_report', label: 'Error Report', icon: '🚨' },
]

export function FeedbackModal() {
  const {
    open,
    type,
    description,
    sending,
    error,
    success,
    errorInfo,
    chatContext,
    screenshots,
    closeModal,
    setType,
    setDescription,
    addScreenshots,
    removeScreenshot,
    submit,
  } = useFeedback()

  const fileInputRef = useRef<HTMLInputElement>(null)
  const isMobile = useIsCompactLayout()

  const characterCount = description.length
  const disabled = sending || (characterCount === 0 && !errorInfo && !chatContext)

  // Close on escape key
  useEscapeKey(closeModal, open)

  const handleSubmit = async () => {
    await submit()
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files && files.length > 0) {
      addScreenshots(files)
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleRemoveScreenshot = (index: number) => {
    removeScreenshot(index)
  }

  if (!open) return null

  return (
    <>
      {/* Full-screen backdrop */}
      <div
        className="fixed inset-0 z-[100] bg-black/40 transition"
        onClick={closeModal}
      />

      {/* Modal container */}
      <div
        className={`fixed inset-0 ${isMobile ? '' : 'md:left-[320px]'} z-[100] flex ${isMobile ? 'items-start justify-start' : 'items-center justify-center px-4'}`}
        role="dialog"
        aria-modal="true"
      >
        <div className={`w-full ${isMobile ? 'h-screen' : 'max-w-lg'} ${isMobile ? 'rounded-none' : 'rounded-3xl'} border border-border bg-surface p-6 shadow-card`}>
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-text">Send Feedback</h2>
              <p className="text-sm text-text-secondary">
                Help us improve XAVIOR
              </p>
            </div>
            <button
              type="button"
              onClick={closeModal}
              className="rounded-full border border-border p-2 text-text-muted transition hover:border-accent hover:text-accent-strong"
            >
              <span className="sr-only">Close</span>
              <span className="text-lg">×</span>
            </button>
          </div>

          {/* Type selector */}
          <div className="mt-4 flex flex-wrap gap-2">
            {feedbackTypes.map((ft) => (
              <button
                key={ft.value}
                type="button"
                onClick={() => setType(ft.value)}
                className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition ${
                  type === ft.value
                    ? 'bg-accent text-white'
                    : 'bg-surface-muted text-text-secondary hover:bg-surface-hover'
                }`}
              >
                <span>{ft.icon}</span>
                <span>{ft.label}</span>
              </button>
            ))}
          </div>

          {/* Error context preview */}
          {errorInfo && (
            <div className="mt-4 rounded-lg bg-red-50 border border-red-200 p-3">
              <p className="text-sm font-medium text-red-800">Error captured:</p>
              <p className="mt-1 text-sm text-red-700 font-mono truncate">
                {errorInfo.message}
              </p>
            </div>
          )}

          {/* Chat context preview */}
          {chatContext && (
            <div className="mt-4 rounded-lg bg-blue-50 border border-blue-200 p-3">
              <p className="text-sm font-medium text-blue-800">About this response:</p>
              {chatContext.assistantResponse && (
                <p className="mt-1 text-sm text-blue-700 line-clamp-2">
                  "{chatContext.assistantResponse.slice(0, 150)}..."
                </p>
              )}
            </div>
          )}

          {/* Description textarea */}
          <textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder={
              type === 'bug_report'
                ? "What happened? What did you expect?"
                : type === 'feature_request'
                ? "What would you like XAVIOR to do?"
                : type === 'chat_response'
                ? "What was wrong with this response?"
                : "Please describe the issue..."
            }
            rows={4}
            className="mt-4 w-full rounded-2xl border border-border bg-surface-muted px-4 py-3 text-sm text-text shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
            disabled={sending}
          />

          {/* Character count and status */}
          <div className="mt-2 flex items-center justify-between text-xs text-text-muted">
            <span>{characterCount} / 2000</span>
            {error && <span className="text-rose-600">{error}</span>}
            {success && <span className="text-emerald-600">Thank you!</span>}
          </div>

          {/* Screenshot section */}
          <div className="mt-4">
            {screenshots.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {screenshots.map((screenshot, index) => (
                  <div key={index} className="relative inline-block">
                    <img
                      src={screenshot.preview}
                      alt={`Screenshot ${index + 1}`}
                      className="h-20 w-auto rounded-lg border border-border object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveScreenshot(index)}
                      className="absolute -top-2 -right-2 rounded-full bg-red-500 p-1 text-white text-xs hover:bg-red-600"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            <label className="flex cursor-pointer items-center gap-2 text-sm text-text-secondary hover:text-accent transition">
              <span>📷</span>
              <span>{screenshots.length > 0 ? 'Add more screenshots' : 'Add screenshot'}</span>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
            </label>
          </div>

          {/* Action buttons */}
          <div className="mt-5 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={closeModal}
              className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-text transition hover:border-accent hover:text-accent-strong"
              disabled={sending}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-accent-strong disabled:cursor-not-allowed disabled:bg-border"
              disabled={disabled}
            >
              {sending ? 'Sending...' : 'Send Feedback'}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
